// Organization policy denials (spec 6.5, design 9.2 and 12): guardrails in the customer's cloud that refuse a
// change. The engine passes the cloud's message through, so they are recognized by their text here, in one place.

export interface PolicyDenial {
  /** The cloud's name for the callout ("Azure", "AWS", "Google Cloud"). */
  cloud: string;
  /** The policy's name or ID when the message gives it. */
  policy: string | null;
}

function first(text: string, patterns: readonly RegExp[]): string | null {
  for (const pattern of patterns) {
    const match = pattern.exec(text);
    if (match?.[1]) return match[1];
  }
  return null;
}

const AZURE = /RequestDisallowedByPolicy|disallowed by policy/i;
const AZURE_NAME = [
  /"policyDefinitionDisplayName"\s*:\s*"([^"]+)"/i,
  /"policyAssignmentDisplayName"\s*:\s*"([^"]+)"/i,
  /policyDefinitions\/([A-Za-z0-9_.-]+)/i,
  /policyAssignments\/([A-Za-z0-9_.-]+)/i,
  /disallowed by policy[^'"]*['"]([^'"]{1,120})['"]/i,
];
const AWS = /explicit deny in a service control policy|explicit deny in an? (?:organizations? )?SCP/i;
const AWS_NAME = [/(arn:aws:organizations::\d+:policy\/[\w/-]+)/, /\b(p-[a-z0-9]{8,})\b/];
const GCP = /constraints\/[\w.]+|organization polic(?:y|ies)|orgpolicy/i;
const GCP_NAME = [/(constraints\/[\w.]+)/];

/** The first policy denial in the texts (error message, diagnostics, error log lines), else null. */
export function findPolicyDenial(texts: readonly (string | null | undefined)[]): PolicyDenial | null {
  for (const text of texts) {
    if (!text) continue;
    if (AZURE.test(text)) return { cloud: 'Azure', policy: first(text, AZURE_NAME) };
    if (AWS.test(text)) return { cloud: 'AWS', policy: first(text, AWS_NAME) };
    if (GCP.test(text) && /violat|denied|forbid|not allowed|constraint/i.test(text))
      return { cloud: 'Google Cloud', policy: first(text, GCP_NAME) };
  }
  return null;
}
